import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import DescriptionIcon from '@mui/icons-material/Description';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import OutboxIcon from '@mui/icons-material/Outbox';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import MergeIcon from '@mui/icons-material/MergeType';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useUserStore } from '../../store';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import MaventaStatusBadge from './MaventaStatusBadge';
import AccountingTransferBadge from './AccountingTransferBadge';
import AmiliPaymentStatusBadge, { AmiliConflictBadge } from './AmiliPaymentStatusBadge';
import CreateStandaloneInvoiceDialog from './CreateStandaloneInvoiceDialog';
import BankStatementImportDialog from './BankStatementImportDialog';
import PaymentReminderDialog from './PaymentReminderDialog';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  DateRangeFilter,
  getDateRange,
  sanitizeDateString,
  FilterSummary,
} from '../shared/ListFilters';
import DataTable from '../shared/DataTable';
import DecimalField from '../shared/DecimalField';

import { can } from '../../utils/access';
import { PAYMENT_METHODS, paymentMethodLabel } from '../../utils/paymentMethods';
const STORAGE_KEY = 'invoices_list_preferences';

// All available invoice statuses
const allInvoiceStatuses = [
  { value: 'draft', labelKey: 'Draft' },
  { value: 'sent', labelKey: 'Sent' },
  { value: 'paid', labelKey: 'Paid' },
  { value: 'cancelled', labelKey: 'Cancelled' },
  { value: 'combined', labelKey: 'Combined' },
  { value: 'refunded', labelKey: 'Refunded' },
];

// Default statuses to show (excluding cancelled)
const defaultStatuses = ['draft', 'sent', 'paid'];

const defaultPreferences = {
  sortField: 'invoice_date',
  sortDirection: 'desc',
  searchQuery: '',
  filters: {
    status: defaultStatuses,
    overdueOnly: false,
    // Issued invoices holding part of their money (osasuoritus)
    partiallyPaidOnly: false,
    // Paid or credited here while Amili still collects (amili_conflict_at)
    amiliConflictOnly: false,
    paymentType: 'all',
    // Origin: 'all' | 'normal' | 'parts_sale'. Defaults to showing everything —
    // the list is the single source of truth for money in.
    source: 'all',
    dateRangeType: 'all',
    dateFrom: '',
    dateTo: '',
  },
};

// The payment method filter holds 'all', 'cash_or_card' or one Invoice.payment_method value.
// Browsers that used the old hand-written list may still have 'regular' saved, which was
// Tilisiirto. Anything else unknown (hand-edited storage, a removed method) means all invoices.
const normalizePaymentType = (value) => {
  if (value === 'regular') return 'bank_transfer';
  if (value === 'all' || value === 'cash_or_card') return value;
  return PAYMENT_METHODS.some((m) => m.value === value) ? value : 'all';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fi-FI');
};

const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

const dateStringToDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

const dateToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Mirrors Invoice.PAYMENT_ROUNDING_TOLERANCE: an amount this close to the open balance is the whole balance.
const PAYMENT_ROUNDING_TOLERANCE = 0.05;

const MainInvoicesView = ({ t }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [paymentImportOpen, setPaymentImportOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [markPaidDate, setMarkPaidDate] = useState('');
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  // The amount in the dialog is prefilled with the open balance: left as is, the invoice is
  // marked paid; lowered, the amount is recorded as an osasuoritus and the invoice stays open.
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidMethod, setMarkPaidMethod] = useState('bank_transfer');
  const [markPaidError, setMarkPaidError] = useState('');
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [combineDialogOpen, setCombineDialogOpen] = useState(false);
  const [combineLoading, setCombineLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const amiliEnabled = currentOrganization?.amili_enabled || false;

  // Use localStorage-persisted preferences
  const {
    preferences,
    updatePreferences,
    updateFilters,
    resetFilters: resetLocalFilters,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Extract filter values from preferences (with safeguards for invalid localStorage data)
  // Saved preferences may still hold the retired stored status 'overdue' (2026-10-27); drop
  // anything the backend no longer knows so the dropdown and the chip count agree.
  const statusFilter = Array.isArray(preferences.filters.status)
    ? preferences.filters.status.filter((s) => allInvoiceStatuses.some((o) => o.value === s))
    : defaultStatuses;
  const overdueOnly = preferences.filters.overdueOnly ?? false;
  const partiallyPaidOnly = preferences.filters.partiallyPaidOnly ?? false;
  const amiliConflictOnly = preferences.filters.amiliConflictOnly ?? false;
  const paymentType = normalizePaymentType(preferences.filters.paymentType);
  const sourceFilter = preferences.filters.source || 'all';
  const dateRangeType = preferences.filters.dateRangeType || 'all';
  const dateFrom = sanitizeDateString(preferences.filters.dateFrom);
  const dateTo = sanitizeDateString(preferences.filters.dateTo);

  // Convert date range to backend filter params
  const dateFilterParams = useMemo(() => {
    if (dateRangeType === 'all') return {};

    const { from, to } = getDateRange(dateRangeType, dateFrom, dateTo);
    const params = {};
    if (from) params.invoice_date_from = from.toISOString().split('T')[0];
    if (to) params.invoice_date_to = to.toISOString().split('T')[0];
    return params;
  }, [dateRangeType, dateFrom, dateTo]);

  // Build server-side filters
  const serverFilters = useMemo(() => {
    const filters = {};

    // Status filter (multi-select) - send as comma-separated
    if (statusFilter.length > 0 && statusFilter.length < allInvoiceStatuses.length) {
      filters.status = statusFilter;
    }

    // Overdue only
    if (overdueOnly) {
      filters.is_overdue = true;
    }

    // Partially paid only (an issued invoice with part of its money recorded)
    if (partiallyPaidOnly) {
      filters.is_partially_paid = true;
    }

    if (amiliConflictOnly) {
      filters.has_amili_conflict = true;
    }

    // Payment method: a single method, or cash and card together
    if (paymentType === 'cash_or_card') {
      filters.is_cash_or_card_payment = true;
    } else if (paymentType !== 'all') {
      filters.payment_method = paymentType;
    }

    // Invoice origin (work order / standalone vs. parts sale)
    if (sourceFilter !== 'all') {
      filters.source = sourceFilter;
    }

    // Date range
    if (dateFilterParams.invoice_date_from) {
      filters.invoice_date_from = dateFilterParams.invoice_date_from;
    }
    if (dateFilterParams.invoice_date_to) {
      filters.invoice_date_to = dateFilterParams.invoice_date_to;
    }

    return filters;
  }, [statusFilter, overdueOnly, partiallyPaidOnly, amiliConflictOnly, paymentType, sourceFilter, dateFilterParams]);

  // Use server-side pagination hook
  const {
    data: invoices,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    sortField,
    sortDirection,
    handleSort,
    refetch,
    setFilters,
  } = usePaginatedList('/api/invoices/', {
    initialFilters: serverFilters,
    defaultSortField: 'invoice_date',
    defaultSortDirection: 'desc',
    defaultPageSize: 50,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Update server filters when local filters change
  // Note: Page reset is handled automatically by usePaginatedList's effectivePage logic
  React.useEffect(() => {
    setFilters(serverFilters);
  }, [serverFilters, setFilters]);

  // Sync search query from localStorage preferences to hook
  React.useEffect(() => {
    setSearchQuery(preferences.searchQuery || '');
  }, [preferences.searchQuery, setSearchQuery]);

  // Reset all filters (resetLocalFilters also clears searchQuery in preferences)
  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  const handleMenuOpen = (event, invoice) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  const handlePreviewPDF = async () => {
    if (!selectedInvoice) return;
    window.open(`/api/invoices/${selectedInvoice.id}/preview/`, '_blank');
    handleMenuClose();
  };

  const handleDownloadPDF = async () => {
    if (!selectedInvoice) return;
    window.open(`/api/invoices/${selectedInvoice.id}/pdf/`, '_blank');
    handleMenuClose();
  };

  // Maksumuistutus is only offered where the backend would allow it: the organization has
  // the setting on and is not on Amili (whose Reskontravahti already sends reminders), the
  // customer is a company, and the invoice is overdue with an open balance. Offering an
  // action the backend refuses is the silent-failure trap the mark-paid comment warns about.
  const paymentRemindersEnabled = !!currentOrganization?.payment_reminders_enabled;
  const amiliInUse = !!currentOrganization?.amili_enabled
    && currentOrganization?.amili_status === 'active';
  const canRemind = (invoice) => (
    !!invoice
    && paymentRemindersEnabled
    && !amiliInUse
    && invoice.customer_type === 'company'
    && invoice.is_overdue
    && !invoice.settlement_status
    && (parseFloat(invoice.open_balance ?? invoice.total_incl_vat ?? 0) || 0) > 0
    && can(currentOrganization, 'invoices.manage')
  );

  const handlePaymentReminderClick = () => {
    setReminderDialogOpen(true);
    setAnchorEl(null);
  };

  // Open the date dialog rather than marking paid straight away — the payment date is almost
  // never today when a batch of invoices is cleared from the list against a bank statement.
  // Closes the menu but keeps selectedInvoice, which the dialog still needs.
  const handleMarkPaidClick = () => {
    setMarkPaidDate(dateToDateString(new Date()));
    setMarkPaidAmount(String(selectedInvoice?.open_balance ?? selectedInvoice?.total_incl_vat ?? ''));
    setMarkPaidMethod(selectedInvoice?.payment_method || 'bank_transfer');
    setMarkPaidError('');
    setMarkPaidDialogOpen(true);
    setAnchorEl(null);
  };

  // The open balance the dialog measures the typed amount against, and whether that amount
  // covers it (within Invoice.PAYMENT_ROUNDING_TOLERANCE, the Finnish cash rounding unit).
  const markPaidOpenBalance = parseFloat(selectedInvoice?.open_balance ?? selectedInvoice?.total_incl_vat ?? 0) || 0;
  const markPaidAmountValue = parseFloat(markPaidAmount);
  const markPaidCovers = !Number.isNaN(markPaidAmountValue)
    && markPaidAmountValue >= markPaidOpenBalance - PAYMENT_ROUNDING_TOLERANCE;
  // An osasuoritus is a bank transfer only for now (cash and card partials are coming), so
  // below the open balance the method is fixed; a full payment keeps the invoice's own method.
  const markPaidIsPartial = !markPaidCovers && markPaidAmountValue > 0;
  const markPaidEffectiveMethod = markPaidIsPartial ? 'bank_transfer' : markPaidMethod;

  const handleMarkPaidDialogClose = () => {
    setMarkPaidDialogOpen(false);
    setSelectedInvoice(null);
  };

  // One dialog for both: the full open balance goes through mark_paid exactly as before,
  // anything smaller is an osasuoritus through record_payment. Over the balance and zero
  // are refused here, with the same messages the ledger would give.
  const handleMarkPaidConfirm = async () => {
    if (!selectedInvoice) return;
    if (Number.isNaN(markPaidAmountValue) || (!markPaidCovers && markPaidAmountValue <= 0)) {
      setMarkPaidError(t('invoices.errorPaymentZero'));
      return;
    }
    if (markPaidAmountValue > markPaidOpenBalance + PAYMENT_ROUNDING_TOLERANCE) {
      setMarkPaidError(t('invoices.errorPaymentExceedsOpenBalance'));
      return;
    }
    setMarkPaidError('');
    setMarkPaidLoading(true);
    try {
      if (markPaidCovers) {
        await axios.post(`/api/invoices/${selectedInvoice.id}/mark_paid/`, {
          paid_date: markPaidDate || undefined,
        });
        setSnackbar({ open: true, message: t('Invoice marked as paid'), severity: 'success' });
      } else {
        await axios.post(`/api/invoices/${selectedInvoice.id}/record_payment/`, {
          amount: markPaidAmount,
          payment_date: markPaidDate,
          payment_method: markPaidEffectiveMethod,
        });
        setSnackbar({ open: true, message: t('invoices.paymentRecorded'), severity: 'success' });
      }
      handleMarkPaidDialogClose();
      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: markPaidCovers
          ? t('Failed to mark invoice as paid')
          : (error.response?.data?.error || t('invoices.paymentRecordFailed')),
        severity: 'error',
      });
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
    setAnchorEl(null);
  };

  const handleCancelConfirm = async () => {
    if (!selectedInvoice) return;
    setCancelLoading(true);
    try {
      await axios.post(`/api/invoices/${selectedInvoice.id}/cancel/`);
      refetch();
      setCancelDialogOpen(false);
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Error cancelling invoice:', error);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleInvoiceCreated = (invoice) => {
    setCreateDialogOpen(false);
    navigate(`/invoices/${invoice.id}`);
  };

  // Combine invoices logic
  const selectedInvoicesList = useMemo(() => {
    if (!invoices || selectedInvoiceIds.size === 0) return [];
    return invoices.filter((inv) => selectedInvoiceIds.has(inv.id));
  }, [invoices, selectedInvoiceIds]);

  const canCombine = useMemo(() => {
    if (selectedInvoicesList.length < 2) return { enabled: false, reason: '' };
    const allDraft = selectedInvoicesList.every((inv) => inv.status === 'draft');
    if (!allDraft) return { enabled: false, reason: t('Only draft invoices can be combined') };
    const firstName = selectedInvoicesList[0].customer_name;
    const sameCustomer = selectedInvoicesList.every((inv) => inv.customer_name === firstName);
    if (!sameCustomer) return { enabled: false, reason: t('Combined invoice can only be created from invoices of the same customer') };
    return { enabled: true, reason: '' };
  }, [selectedInvoicesList, t]);

  const selectionActions = useMemo(() => {
    if (selectedInvoiceIds.size < 2) return [];
    return [
      {
        label: t('Create combined invoice'),
        icon: <MergeIcon fontSize="small" />,
        onClick: () => setCombineDialogOpen(true),
        disabled: !canCombine.enabled,
        tooltip: canCombine.reason || t('Select draft invoices from the same customer to create a combined invoice'),
        variant: 'contained',
      },
    ];
  }, [selectedInvoiceIds.size, canCombine.enabled, canCombine.reason, t]);

  const handleCombineConfirm = async () => {
    setCombineLoading(true);
    try {
      const response = await axios.post('/api/invoices/combine/', {
        invoice_ids: Array.from(selectedInvoiceIds),
      });
      setCombineDialogOpen(false);
      setSelectedInvoiceIds(new Set());
      setSnackbar({ open: true, message: t('Combined invoice created'), severity: 'success' });
      navigate(`/invoices/${response.data.id}`);
    } catch (error) {
      const msg = error.response?.data?.error || t('Error creating combined invoice');
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setCombineLoading(false);
    }
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    // Check if statuses filter is different from default
    const statusesChanged = statusFilter.length !== defaultStatuses.length ||
      !statusFilter.every((s) => defaultStatuses.includes(s));
    if (statusesChanged) count++;
    if (overdueOnly) count++;
    if (partiallyPaidOnly) count++;
    if (amiliConflictOnly) count++;
    if (paymentType !== 'all') count++;
    if (sourceFilter !== 'all') count++;
    if (dateRangeType !== 'all') count++;
    return count;
  }, [statusFilter, overdueOnly, partiallyPaidOnly, amiliConflictOnly, paymentType, sourceFilter, dateRangeType]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];

    // Statuses (if different from default)
    const statusesChanged = statusFilter.length !== defaultStatuses.length ||
      !statusFilter.every((s) => defaultStatuses.includes(s));
    if (statusesChanged) {
      if (statusFilter.length === 0) {
        chips.push({ key: 'statuses', label: t('No statuses selected') });
      } else if (statusFilter.length <= 2) {
        chips.push({ key: 'statuses', label: statusFilter.map((s) => t(s.charAt(0).toUpperCase() + s.slice(1))).join(', ') });
      } else {
        chips.push({ key: 'statuses', label: t('{{count}} statuses', { count: statusFilter.length }) });
      }
    }

    if (overdueOnly) {
      chips.push({ key: 'overdue', label: t('Overdue') });
    }

    if (partiallyPaidOnly) {
      chips.push({ key: 'partiallyPaid', label: t('invoices.filterPartiallyPaid') });
    }

    if (amiliConflictOnly) {
      chips.push({ key: 'amiliConflict', label: t('invoices.filterAmiliConflict') });
    }

    if (paymentType === 'cash_or_card') {
      chips.push({ key: 'paymentType', label: t('Cash/Card payments') });
    } else if (paymentType !== 'all') {
      chips.push({ key: 'paymentType', label: paymentMethodLabel(t, paymentType) });
    }

    if (sourceFilter === 'parts_sale') {
      chips.push({ key: 'source', label: t('nav.partsSales') });
    } else if (sourceFilter === 'normal') {
      chips.push({ key: 'source', label: t('Work orders and invoices') });
    }

    if (dateRangeType === 'today') {
      chips.push({ key: 'date', label: t('Today') });
    } else if (dateRangeType === 'this_week') {
      chips.push({ key: 'date', label: t('This week') });
    } else if (dateRangeType === 'this_month') {
      chips.push({ key: 'date', label: t('This month') });
    } else if (dateRangeType === 'custom') {
      if (dateFrom && dateTo) {
        chips.push({ key: 'date', label: `${dateFrom} – ${dateTo}` });
      } else if (dateFrom) {
        chips.push({ key: 'date', label: `${t('From')} ${dateFrom}` });
      } else if (dateTo) {
        chips.push({ key: 'date', label: `${t('To')} ${dateTo}` });
      }
    }

    return chips;
  }, [statusFilter, overdueOnly, partiallyPaidOnly, amiliConflictOnly, paymentType, sourceFilter, dateRangeType, dateFrom, dateTo, t]);

  // State for error snackbar - cleared when error changes to null
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const prevError = React.useRef(error);

  React.useEffect(() => {
    // Show snackbar when a new error appears
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box data-testid="invoices-page">
      {/* Error Snackbar - shows error without blocking the UI */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {t('Error')}: {error}
        </Alert>
      </Snackbar>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Invoices')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {can(currentOrganization, 'invoices.mark_paid') && (
            <Button
              variant="outlined"
              color="primary"
              data-testid="import-payments-button"
              startIcon={<PaymentsIcon />}
              onClick={() => setPaymentImportOpen(true)}
              sx={{ fontWeight: 600 }}
            >
              {t('Import payments')}
            </Button>
          )}
          {can(currentOrganization, 'invoices.manage') && (
            <Button
              variant="contained"
              color="primary"
              data-testid="add-invoice-button"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ fontWeight: 600 }}
            >
              {t('Create invoice')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery || ''}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('Search by invoice number, customer...')}
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('Status')}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>{t('Status')}</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={(e) => updateFilters({ status: e.target.value })}
              label={t('Status')}
              renderValue={(selected) => {
                if (selected.length === allInvoiceStatuses.length) return t('All');
                if (selected.length === 0) return t('None');
                return selected.map((s) => t(s.charAt(0).toUpperCase() + s.slice(1))).join(', ');
              }}
            >
              {allInvoiceStatuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  <Checkbox checked={statusFilter.includes(status.value)} />
                  <ListItemText primary={t(status.labelKey)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FilterSection>

        <FilterSection title={t('Quick Filters')} dividerTop>
          <FilterChip
            icon={<WarningIcon />}
            label={t('Overdue')}
            active={overdueOnly}
            onClick={() => updateFilters({ overdueOnly: !overdueOnly })}
            color="error"
          />
          <FilterChip
            icon={<PaymentsIcon />}
            label={t('invoices.filterPartiallyPaid')}
            active={partiallyPaidOnly}
            onClick={() => updateFilters({ partiallyPaidOnly: !partiallyPaidOnly })}
            color="warning"
          />
          {amiliEnabled && (
            <FilterChip
              icon={<WarningIcon />}
              label={t('invoices.filterAmiliConflict')}
              active={amiliConflictOnly}
              onClick={() => updateFilters({ amiliConflictOnly: !amiliConflictOnly })}
              color="error"
            />
          )}
        </FilterSection>

        <FilterSection title={t('Payment Type')} dividerTop>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('Payment Type')}</InputLabel>
            <Select
              value={paymentType}
              onChange={(e) => updateFilters({ paymentType: e.target.value })}
              label={t('Payment Type')}
            >
              <MenuItem value="all">{t('All')}</MenuItem>
              {PAYMENT_METHODS.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {t(method.labelKey)}
                </MenuItem>
              ))}
              <MenuItem value="cash_or_card">{t('Cash/Card payments')}</MenuItem>
            </Select>
          </FormControl>
        </FilterSection>

        <FilterSection title={t('Invoice Source')} dividerTop>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('Invoice Source')}</InputLabel>
            <Select
              value={sourceFilter}
              onChange={(e) => updateFilters({ source: e.target.value })}
              label={t('Invoice Source')}
            >
              <MenuItem value="all">{t('All')}</MenuItem>
              <MenuItem value="normal">{t('Work orders and invoices')}</MenuItem>
              <MenuItem value="parts_sale">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PointOfSaleIcon fontSize="small" color="info" />
                  {t('nav.partsSales')}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </FilterSection>

        <FilterSection title={t('Invoice Date')} dividerTop>
          <DateRangeFilter
            dateRangeType={dateRangeType}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onTypeChange={(type) => {
              if (type === 'all' || type !== 'custom') {
                updateFilters({ dateRangeType: type, dateFrom: '', dateTo: '' });
              } else {
                updateFilters({ dateRangeType: type });
              }
            }}
            onFromChange={(date) => {
              updateFilters({ dateRangeType: 'custom', dateFrom: date });
            }}
            onToChange={(date) => {
              updateFilters({ dateRangeType: 'custom', dateTo: date });
            }}
            t={t}
          />
        </FilterSection>
      </FilterPanel>

      {/* Desktop Invoice Table */}
      <DataTable
        columns={[
          {
            key: 'invoice_number',
            label: t('Invoice'),
            sortable: true,
            noWrap: true,
            render: (invoice) => (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 500, ...numFont, fontSize: '13px' }}>
                    {invoice.invoice_number}
                  </Typography>
                  {(invoice.payment_method === 'cash' || invoice.payment_method === 'card') && (
                    <Tooltip title={invoice.payment_method === 'cash' ? t('Cash payment') : t('Card payment')}>
                      <PaymentsIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    </Tooltip>
                  )}
                  {invoice.payment_method === 'financing' && (
                    <Tooltip title={t('financingPayment')}>
                      <AccountBalanceIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    </Tooltip>
                  )}
                  {invoice.payment_method === 'invoiced_elsewhere' && (
                    <Tooltip title={t('invoices.invoicedElsewhereMethod')}>
                      <OutboxIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <InvoiceStatusBadge status={invoice.is_overdue ? 'overdue' : invoice.status} invoice_type={invoice.invoice_type} settlement_status={invoice.settlement_status} partially_paid={invoice.is_partially_settled || invoice.is_partially_paid} />
                  {/* Parts-sale invoices share the invoice numbering, so they belong in this
                      list - the badge tells them apart at a glance. */}
                  {invoice.source === 'parts_sale' && (
                    <Chip
                      icon={<PointOfSaleIcon sx={{ fontSize: 14 }} />}
                      label={t('nav.partsSales')}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '11px' } }}
                    />
                  )}
                  {/* Delivery can fail after the invoice is sent - surface it here so it is
                      not only visible on the detail page. */}
                  {invoice.maventa_status === 'failed' && (
                    <MaventaStatusBadge
                      status={invoice.maventa_status}
                      deliveryMethod={invoice.delivery_method}
                      errorMessage={invoice.maventa_error_message}
                    />
                  )}
                  {/* Accounting transfer (Netvisor etc.) failures surface here too. */}
                  {invoice.accounting_transfer_status === 'failed' && (
                    <AccountingTransferBadge
                      status={invoice.accounting_transfer_status}
                      errorMessage={invoice.accounting_transfer_error}
                    />
                  )}
                </Box>
              </Box>
            ),
          },
          {
            key: 'customer_name',
            label: t('Customer'),
            sortable: true,
            maxWidth: 200,
            render: (invoice) => (
              <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>
                {invoice.customer_name}
              </Typography>
            ),
          },
          {
            key: 'vehicle_registration',
            label: t('Vehicle'),
            sortable: true,
            width: '1%',
            noWrap: true,
            hideBelow: 'lg',
            render: (invoice) => (
              <Typography sx={{ fontSize: '13px', ...numFont, color: 'text.secondary' }}>
                {invoice.vehicle_registration || '-'}
              </Typography>
            ),
          },
          {
            key: 'invoice_date',
            label: t('Date'),
            sortable: true,
            width: '1%',
            noWrap: true,
            render: (invoice) => (
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {formatDate(invoice.invoice_date)}
              </Typography>
            ),
          },
          {
            key: 'due_date',
            label: t('Due'),
            sortable: true,
            width: '1%',
            noWrap: true,
            hideBelow: 'lg',
            render: (invoice) => (
              <Typography
                sx={{
                  fontSize: '13px',
                  color: invoice.is_overdue ? 'error.main' : 'text.secondary',
                  fontWeight: invoice.is_overdue ? 500 : 400,
                }}
              >
                {formatDate(invoice.due_date)}
              </Typography>
            ),
          },
          {
            key: 'total_incl_vat',
            label: t('Total'),
            sortable: true,
            align: 'right',
            width: '1%',
            noWrap: true,
            render: (invoice) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {formatCurrency(invoice.total_incl_vat)}
                </Typography>
                {invoice.is_partially_paid && (
                  <Typography
                    variant="caption"
                    color="warning.main"
                    data-testid={`invoice-open-balance-${invoice.id}`}
                  >
                    {t('invoices.openBalance')} {formatCurrency(invoice.open_balance)}
                  </Typography>
                )}
              </Box>
            ),
          },
          ...(amiliEnabled
            ? [
                {
                  key: 'amili_status',
                  label: t('amili.paymentStatus', 'Maksuvalvonta'),
                  render: (invoice) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <AmiliPaymentStatusBadge status={invoice.amili_payment_status} />
                      {invoice.amili_conflict_at && <AmiliConflictBadge kind={invoice.amili_conflict_kind} />}
                    </Box>
                  ),
                },
              ]
            : []),
          {
            key: 'actions',
            label: '',
            width: '1%',
            render: (invoice) => (
              <IconButton
                onClick={(e) => handleMenuOpen(e, invoice)}
                sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]}
        data={invoices}
        loading={loading}
        onRowClick={(invoice) => navigate(`/invoices/${invoice.id}`)}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        emptyIcon={<DescriptionIcon />}
        emptyTitle={searchQuery || activeFilterCount > 0 ? t('No invoices match your search') : t('No invoices yet')}
        emptyDescription={t('Create invoices from completed maintenances')}
        selectable
        selectedIds={selectedInvoiceIds}
        onSelectionChange={setSelectedInvoiceIds}
        selectionActions={selectionActions}
        testIdPrefix="invoice-row"
        tableTestId="invoices-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}>
        {/* Loading overlay for refetch */}
        {loading && invoices.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && invoices.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : invoices.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {searchQuery || activeFilterCount > 0 ? t('No invoices match your search') : t('No invoices yet')}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              {t('Create invoices from completed maintenances')}
            </Typography>
          </Paper>
        ) : (
          <>
            {invoices.map((invoice) => (
              <Paper
                key={invoice.id}
                data-testid={`invoice-row-${invoice.id}`}
                elevation={0}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
                sx={{
                  p: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                    {/* Invoice Icon */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: 'rgba(49, 130, 206, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'secondary.main',
                        flexShrink: 0,
                      }}
                    >
                      <DescriptionIcon />
                    </Box>

                    {/* Invoice Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            ...numFont,
                            color: 'text.primary',
                          }}
                        >
                          {invoice.invoice_number}
                        </Typography>
                        <InvoiceStatusBadge status={invoice.is_overdue ? 'overdue' : invoice.status} invoice_type={invoice.invoice_type} settlement_status={invoice.settlement_status} partially_paid={invoice.is_partially_settled || invoice.is_partially_paid} />
                        {invoice.source === 'parts_sale' && (
                          <Chip
                            icon={<PointOfSaleIcon sx={{ fontSize: 14 }} />}
                            label={t('nav.partsSales')}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '11px' } }}
                          />
                        )}
                        {invoice.maventa_status === 'failed' && (
                          <MaventaStatusBadge
                            status={invoice.maventa_status}
                            deliveryMethod={invoice.delivery_method}
                            errorMessage={invoice.maventa_error_message}
                          />
                        )}
                        {invoice.accounting_transfer_status === 'failed' && (
                          <AccountingTransferBadge
                            status={invoice.accounting_transfer_status}
                            errorMessage={invoice.accounting_transfer_error}
                          />
                        )}
                        {amiliEnabled && invoice.amili_payment_status && (
                          <AmiliPaymentStatusBadge status={invoice.amili_payment_status} />
                        )}
                        {amiliEnabled && invoice.amili_conflict_at && <AmiliConflictBadge kind={invoice.amili_conflict_kind} />}
                        {(invoice.payment_method === 'cash' || invoice.payment_method === 'card') && (
                          <Tooltip title={invoice.payment_method === 'cash' ? t('Cash payment') : t('Card payment')}>
                            <PaymentsIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          </Tooltip>
                        )}
                        {invoice.payment_method === 'financing' && (
                          <Tooltip title={t('financingPayment')}>
                            <AccountBalanceIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          </Tooltip>
                        )}
                        {invoice.payment_method === 'invoiced_elsewhere' && (
                          <Tooltip title={t('invoices.invoicedElsewhereMethod')}>
                            <OutboxIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: 'text.primary',
                          mb: 0.25,
                        }}
                      >
                        {invoice.customer_name}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {invoice.vehicle_registration && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <DirectionsCarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography sx={{ fontSize: '12px', ...numFont, color: 'text.secondary' }}>
                              {invoice.vehicle_registration}
                            </Typography>
                          </Box>
                        )}
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                          {t('Due')}: {formatDate(invoice.due_date)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Amount and Actions */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary' }}>
                      {formatCurrency(invoice.total_incl_vat)}
                    </Typography>
                    {invoice.is_partially_paid && (
                      <Typography variant="caption" color="warning.main">
                        {t('invoices.openBalance')} {formatCurrency(invoice.open_balance)}
                      </Typography>
                    )}
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, invoice)}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                      size="small"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}

            {/* Mobile Pagination Controls */}
            <PaginationControls
              page={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              loading={loading}
            />
          </>
        )}
      </Box>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handlePreviewPDF}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Preview PDF')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadPDF}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Download PDF')}</ListItemText>
        </MenuItem>
        {/* Same conditions as the detail view's button: a credited invoice has nothing left to
            pay, and the backend refuses it — offering it here only produced a silent failure. */}
        {selectedInvoice
          && (selectedInvoice.status === 'sent' || selectedInvoice.is_overdue)
          && !selectedInvoice.amili_assignment_id
          && !selectedInvoice.settlement_status
          && can(currentOrganization, 'invoices.mark_paid') && (
          <MenuItem onClick={handleMarkPaidClick}>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>{t('Mark as paid')}</ListItemText>
          </MenuItem>
        )}
        {/* Maksumuistutus. Mirrors reminder_eligibility() on the backend: the org flag is
            on, the org is not on Amili (which runs its own reminders), the customer is a
            company and the invoice is overdue with something still open. */}
        {canRemind(selectedInvoice) && (
          <MenuItem onClick={handlePaymentReminderClick}>
            <ListItemIcon>
              <WarningIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>{t('invoices.sendPaymentReminder')}</ListItemText>
          </MenuItem>
        )}
        {selectedInvoice && (
          selectedInvoice.status === 'draft' ||
          ((selectedInvoice.status === 'sent' || selectedInvoice.status === 'paid') &&
           !selectedInvoice.maventa_invoice_id &&
           selectedInvoice.delivery_method !== 'verkkolasku' &&
           (selectedInvoice.is_cash_or_card_payment || selectedInvoice.delivery_method === 'email' || selectedInvoice.delivery_method === 'pdf'))
        ) && (
          <MenuItem onClick={handleCancelClick}>
            <ListItemIcon>
              <CancelIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('Cancel invoice')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <PaymentReminderDialog
        open={reminderDialogOpen}
        invoice={selectedInvoice}
        onClose={() => setReminderDialogOpen(false)}
        onSent={() => refetch()}
        onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
      />

      {/* Mark as Paid Dialog — mirrors the one in InvoiceDetailView */}
      <Dialog
        open={markPaidDialogOpen}
        onClose={() => !markPaidLoading && handleMarkPaidDialogClose()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('Mark as paid')}</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <DialogContentText sx={{ mb: 2 }}>
              {t('Invoice {{number}} — {{amount}}', {
                number: selectedInvoice.invoice_number,
                amount: formatCurrency(selectedInvoice.total_incl_vat),
              })}
              {parseFloat(selectedInvoice.amount_paid || 0) > 0 && (
                <>
                  {' '}
                  ({t('invoices.paidAndOpen', {
                    paid: formatCurrency(selectedInvoice.amount_paid),
                    remaining: formatCurrency(selectedInvoice.open_balance),
                  })})
                </>
              )}
            </DialogContentText>
          )}
          <DecimalField
            margin="dense"
            fullWidth
            label={t('invoices.paymentAmount')}
            value={markPaidAmount}
            onChange={(value) => {
              setMarkPaidAmount(value);
              setMarkPaidError('');
            }}
            inputProps={{ 'data-testid': 'mark-paid-amount' }}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="list-mark-paid-method-label">{t('Payment method')}</InputLabel>
            <Select
              labelId="list-mark-paid-method-label"
              label={t('Payment method')}
              value={markPaidEffectiveMethod}
              onChange={(e) => setMarkPaidMethod(e.target.value)}
              disabled={markPaidCovers || markPaidIsPartial}
              inputProps={{ 'data-testid': 'mark-paid-method' }}
            >
              <MenuItem value="bank_transfer">{t('Bank transfer')}</MenuItem>
              <MenuItem value="cash">{t('Cash payment')}</MenuItem>
              <MenuItem value="card">{t('Card payment')}</MenuItem>
              <MenuItem value="financing">{t('financingPayment')}</MenuItem>
            </Select>
            {markPaidIsPartial && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('invoices.partialBankTransferOnly')}
              </Typography>
            )}
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              label={t('Paid date')}
              value={dateStringToDate(markPaidDate)}
              onChange={(newValue) => setMarkPaidDate(dateToDateString(newValue))}
              format="dd.MM.yyyy"
              slotProps={{
                textField: { margin: 'dense', fullWidth: true },
              }}
            />
          </LocalizationProvider>
          {markPaidError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="mark-paid-error">
              {markPaidError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMarkPaidDialogClose} disabled={markPaidLoading}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkPaidConfirm}
            disabled={markPaidLoading || !markPaidDate || markPaidAmount === ''}
          >
            {markPaidLoading ? <CircularProgress size={24} /> : t('Mark as paid')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('Cancel invoice?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Are you sure you want to cancel this invoice? This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} disabled={cancelLoading}>
            {t('No, go back')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelConfirm}
            disabled={cancelLoading}
          >
            {cancelLoading ? <CircularProgress size={24} /> : t('Yes, cancel invoice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Invoice Dialog */}
      <CreateStandaloneInvoiceDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onInvoiceCreated={handleInvoiceCreated}
      />

      {/* Bank Statement Payment Import Dialog */}
      <BankStatementImportDialog
        open={paymentImportOpen}
        onClose={() => setPaymentImportOpen(false)}
        onCompleted={() => {
          setPaymentImportOpen(false);
          refetch();
        }}
      />

      {/* Combine Confirmation Dialog */}
      <Dialog
        open={combineDialogOpen}
        onClose={() => !combineLoading && setCombineDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('Create combined invoice?')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Creating combined invoice from {{count}} invoices. Original invoices will be marked as combined and can no longer be sent. Continue?', { count: selectedInvoiceIds.size })}
          </DialogContentText>
          {!canCombine.enabled && canCombine.reason && (
            <Alert severity="warning" sx={{ mt: 2 }}>{canCombine.reason}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCombineDialogOpen(false)} disabled={combineLoading}>
            {t('No, go back')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCombineConfirm}
            disabled={combineLoading || !canCombine.enabled}
          >
            {combineLoading ? <CircularProgress size={24} /> : t('Yes, create combined invoice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default withTranslation()(MainInvoicesView);
